(function () {
  'use strict';

  const SUPABASE_URL =
    'https://xoolcbbpnvdylcoorlqg.supabase.co';

  const PUBLISHABLE_KEY =
    'sb_publishable_bjOrV5qp2n0PtuVFOSA_6g_3Micws2T';


  /* =========================================================
     DATA CACHE
  ========================================================= */

  let reviewRowsCache = null;
  let reviewRowsPromise = null;


  /* =========================================================
     SESSION
  ========================================================= */

  function getSession() {
    try {
      const key = Object.keys(localStorage).find(
        (k) =>
          k.includes('sb-') &&
          k.endsWith('-auth-token')
      );

      if (!key) return null;

      return JSON.parse(
        localStorage.getItem(key)
      );
    } catch (error) {
      console.error(
        '[Corevian Review Monitor] Session error:',
        error
      );

      return null;
    }
  }


  function getAccessToken() {
    return getSession()?.access_token || null;
  }


  /* =========================================================
     SUPABASE RPC
  ========================================================= */

  async function rpc(functionName, payload = {}) {
  const client = getSupabaseClient();

  if (!client) {
    throw new Error('Supabase client is not available.');
  }

  const {
    data,
    error
  } = await client.rpc(
    functionName,
    payload
  );

  if (error) {
    throw new Error(
      error.message ||
      error.details ||
      error.hint ||
      'Administrative request failed.'
    );
  }

  return data;
}


  /* =========================================================
     REVIEW DATA
  ========================================================= */

  async function getReviewRows(forceRefresh = false) {
    if (
      reviewRowsCache &&
      !forceRefresh
    ) {
      return reviewRowsCache;
    }

    if (
      reviewRowsPromise &&
      !forceRefresh
    ) {
      return reviewRowsPromise;
    }

    reviewRowsPromise = rpc(
      'get_admin_document_review_monitor',
      {}
    );

    try {
      const rows =
        await reviewRowsPromise;

      if (!Array.isArray(rows)) {
        throw new Error(
          'The review monitor returned an unexpected response.'
        );
      }

      reviewRowsCache = rows;

      return rows;

    } finally {
      reviewRowsPromise = null;
    }
  }


  function clearReviewCache() {
    reviewRowsCache = null;
  }


  /* =========================================================
     COUNT REVIEW STATUSES
  ========================================================= */

  function getReviewCounts(rows) {
    const counts = {
      overdue: 0,
      dueToday: 0,
      dueSoon: 0,
      current: 0,
      notScheduled: 0
    };

    for (const row of rows || []) {
      const status =
        String(
          row.review_status || ''
        )
          .toLowerCase()
          .trim();

      if (status === 'overdue') {
        counts.overdue += 1;

      } else if (status === 'due today') {
        counts.dueToday += 1;

      } else if (status === 'due soon') {
        counts.dueSoon += 1;

      } else if (status === 'current') {
        counts.current += 1;

      } else if (
        status === 'not scheduled'
      ) {
        counts.notScheduled += 1;
      }
    }

    return counts;
  }


  /* =========================================================
     DISPLAY HELPERS
  ========================================================= */

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }


  function formatDate(value) {
    if (!value) return '—';

    const date =
      new Date(`${value}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
      return escapeHtml(value);
    }

    return date.toLocaleDateString(
      undefined,
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }
    );
  }


  function formatDateTime(value) {
    if (!value) return '—';

    const date =
      new Date(value);

    if (Number.isNaN(date.getTime())) {
      return escapeHtml(value);
    }

    return date.toLocaleString(
      undefined,
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }
    );
  }


  function getStatusClass(status) {
    const normalized =
      String(status || '')
        .toLowerCase()
        .trim();

    if (normalized === 'overdue') {
      return 'cvn-review-status-overdue';
    }

    if (normalized === 'due today') {
      return 'cvn-review-status-today';
    }

    if (normalized === 'due soon') {
      return 'cvn-review-status-soon';
    }

    if (normalized === 'current') {
      return 'cvn-review-status-current';
    }

    return 'cvn-review-status-unscheduled';
  }


  function formatDays(row) {
    if (
      row.days_until_review === null ||
      row.days_until_review === undefined
    ) {
      return '—';
    }

    const days =
      Number(row.days_until_review);

    if (Number.isNaN(days)) {
      return '—';
    }

    if (days < 0) {
      const absolute =
        Math.abs(days);

      return `${absolute} day${
        absolute === 1 ? '' : 's'
      } overdue`;
    }

    if (days === 0) {
      return 'Due today';
    }

    return `${days} day${
      days === 1 ? '' : 's'
    } remaining`;
  }


  /* =========================================================
     STYLES
  ========================================================= */

  function injectStyles() {
    if (
      document.getElementById(
        'cvnReviewMonitorStyles'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'cvnReviewMonitorStyles';

    style.textContent = `

      /* =====================================================
         COMMAND CENTER REVIEW GOVERNANCE
      ===================================================== */

      #cvnReviewGovernanceSummary {
        width: 100%;
        box-sizing: border-box;
        margin: 18px 0 28px;
      }


      .cvn-review-governance-header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 12px;
      }


      .cvn-review-governance-heading {
        color: #111827;
        font-size: 14px;
        font-weight: 800;
        letter-spacing: .08em;
        text-transform: uppercase;
      }


      .cvn-review-governance-link {
        appearance: none;
        border: 0;
        background: transparent;
        color: #147c79;
        padding: 0;
        font: inherit;
        font-size: 13px;
        font-weight: 800;
        cursor: pointer;
      }


      .cvn-review-governance-link:hover {
        text-decoration: underline;
      }


      .cvn-review-governance-grid {
        display: grid;
        grid-template-columns:
          repeat(3, minmax(0, 1fr));
        gap: 16px;
      }


      .cvn-review-governance-card {
        appearance: none;
        width: 100%;
        min-width: 0;
        text-align: left;
        padding: 18px 20px;
        background: #ffffff;
        border: 1px solid #dce3ec;
        border-radius: 14px;
        cursor: pointer;
        transition:
          border-color .18s ease,
          transform .18s ease,
          box-shadow .18s ease;
        font-family: inherit;
      }


      .cvn-review-governance-card:hover {
        border-color: #19B5B0;
        transform: translateY(-1px);
        box-shadow: 0 8px 24px rgba(11,20,38,.07);
      }


      .cvn-review-governance-card-label {
        display: block;
        margin-bottom: 10px;
        color: #6d7b91;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: .06em;
        text-transform: uppercase;
      }


      .cvn-review-governance-card-value {
        display: block;
        color: #111827;
        font-size: 30px;
        font-weight: 800;
        line-height: 1;
      }


      .cvn-review-governance-card-note {
        display: block;
        margin-top: 9px;
        color: #7b8799;
        font-size: 12px;
        line-height: 1.45;
      }


      .cvn-review-governance-card.is-overdue {
        border-color: rgba(190,65,65,.32);
      }


      .cvn-review-governance-card.is-overdue
      .cvn-review-governance-card-value {
        color: #a83636;
      }


      .cvn-review-governance-card.is-due-soon {
        border-color: rgba(189,145,32,.30);
      }


      .cvn-review-governance-card.is-due-soon
      .cvn-review-governance-card-value {
        color: #8c6818;
      }


      .cvn-review-governance-card.is-unscheduled {
        border-color: #d4dce7;
      }


      #cvnReviewGovernanceLoading,
      #cvnReviewGovernanceError {
        padding: 18px 20px;
        background: #ffffff;
        border: 1px solid #dce3ec;
        border-radius: 14px;
        color: #6d7b91;
        font-size: 13px;
      }


      #cvnReviewGovernanceError {
        color: #9e3535;
        border-color: rgba(190,65,65,.32);
      }


      /* =====================================================
         REVIEW MONITOR LAUNCHER
      ===================================================== */

      #cvnReviewMonitorLauncher {
        appearance: none;
        border: 1px solid #536178;
        background: #17243a;
        color: #ffffff;
        font-family: inherit;
        font-size: 14px;
        font-weight: 700;
        padding: 10px 16px;
        min-height: 42px;
        border-radius: 8px;
        cursor: pointer;
        margin-left: 10px;
      }


      #cvnReviewMonitorLauncher:hover {
        border-color: #19B5B0;
      }


      /* =====================================================
         REVIEW MONITOR MODAL
      ===================================================== */

      #cvnReviewMonitorOverlay {
        position: fixed;
        inset: 0;
        z-index: 12700;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        background: rgba(3,8,18,.88);
        box-sizing: border-box;
      }


      #cvnReviewMonitorPanel {
        width: min(1200px,94vw);
        max-width: 94vw;
        max-height: 90vh;
        overflow-y: auto;
        overflow-x: hidden;
        box-sizing: border-box;
        padding: 28px;
        background: #0B1426;
        color: #ffffff;
        border: 1px solid rgba(25,181,176,.40);
        border-radius: 16px;
        box-shadow: 0 24px 70px rgba(0,0,0,.50);
      }


      #cvnReviewMonitorPanel h2 {
        margin: 0 0 8px !important;
        color: #ffffff !important;
        font-size: 28px !important;
        font-weight: 800 !important;
        line-height: 1.2 !important;
      }


      .cvn-review-monitor-subtitle {
        margin: 0 0 22px;
        color: #b9c6d8 !important;
        font-size: 15px !important;
        line-height: 1.6 !important;
      }


      #cvnReviewMonitorMessage {
        display: none;
        margin-bottom: 18px;
        padding: 13px 15px;
        border-radius: 9px;
        font-size: 14px;
      }


      #cvnReviewMonitorMessage.error {
        display: block;
        background: rgba(190,65,65,.13);
        border: 1px solid rgba(210,80,80,.45);
        color: #ffd2d2;
      }


      .cvn-review-summary-grid {
        display: grid;
        grid-template-columns:
          repeat(5, minmax(145px, 1fr));
        gap: 12px;
        margin-bottom: 22px;
      }


      .cvn-review-summary-card {
        padding: 16px;
        background: #101b2e;
        border: 1px solid #2a3a53;
        border-radius: 10px;
      }


      .cvn-review-summary-label {
        margin-bottom: 6px;
        color: #9fb0c6;
        font-size: 11px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: .04em;
      }


      .cvn-review-summary-value {
        color: #ffffff;
        font-size: 25px;
        font-weight: 800;
        line-height: 1.1;
      }


      .cvn-review-table-wrap {
        width: 100%;
        max-width: 100%;
        overflow-x: auto !important;
        overflow-y: hidden;
        box-sizing: border-box;
        border: 1px solid #283750;
        border-radius: 10px;
        background: #0d1728;
        scrollbar-width: auto;
        scrollbar-color: #19B5B0 #111d31;
      }


      .cvn-review-table-wrap::-webkit-scrollbar {
        height: 14px;
      }


      .cvn-review-table-wrap::-webkit-scrollbar-track {
        background: #111d31;
      }


      .cvn-review-table-wrap::-webkit-scrollbar-thumb {
        background: #19B5B0;
        border-radius: 999px;
        border: 3px solid #111d31;
      }


      .cvn-review-table {
        width: max-content !important;
        min-width: 1480px !important;
        border-collapse: collapse;
        background: #0d1728;
        color: #ffffff;
      }


      .cvn-review-table th,
      .cvn-review-table td {
        padding: 13px 14px;
        text-align: left;
        vertical-align: top;
        border-bottom: 1px solid #26354c;
        font-size: 13px;
        line-height: 1.5;
      }


      .cvn-review-table th {
        white-space: nowrap;
        background: #101b2e;
        color: #aab6c9;
        font-size: 12px;
        font-weight: 800;
        text-transform: uppercase;
      }


      .cvn-review-table td {
        color: #e3eaf3;
      }


      .cvn-review-table tr:last-child td {
        border-bottom: 0;
      }


      .cvn-review-document-code {
        color: #ffffff;
        font-weight: 800;
        white-space: nowrap;
      }


      .cvn-review-document-title {
        min-width: 260px;
      }


      .cvn-review-status {
        display: inline-block;
        padding: 6px 10px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 800;
        white-space: nowrap;
      }


      .cvn-review-status-overdue {
        background: rgba(196,61,61,.16);
        color: #ffb8b8;
        border: 1px solid rgba(214,78,78,.50);
      }


      .cvn-review-status-today {
        background: rgba(204,121,31,.16);
        color: #ffd3a0;
        border: 1px solid rgba(219,141,52,.50);
      }


      .cvn-review-status-soon {
        background: rgba(202,169,54,.14);
        color: #f7df8b;
        border: 1px solid rgba(216,185,70,.45);
      }


      .cvn-review-status-current {
        background: rgba(25,181,176,.12);
        color: #75e3df;
        border: 1px solid rgba(25,181,176,.40);
      }


      .cvn-review-status-unscheduled {
        background: rgba(126,142,164,.12);
        color: #b9c6d8;
        border: 1px solid rgba(126,142,164,.35);
      }

       .cvn-review-row-action {
  appearance: none;
  border: 1px solid rgba(25,181,176,.55);
  background: rgba(25,181,176,.10);
  color: #8ef0ec;
  padding: 8px 12px;
  border-radius: 7px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background .18s ease,
    border-color .18s ease;
}

.cvn-review-row-action:hover {
  background: rgba(25,181,176,.18);
  border-color: #19B5B0;
}
      .cvn-review-monitor-note {
        margin-top: 16px;
        padding: 14px 16px;
        background: #101b2e;
        border: 1px solid #2a3a53;
        border-radius: 9px;
        color: #aebdd0;
        font-size: 13px;
        line-height: 1.6;
      }


      .cvn-review-monitor-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        flex-wrap: wrap;
        margin-top: 20px;
      }


      .cvn-review-monitor-actions button {
        min-height: 44px;
        padding: 11px 18px;
        border-radius: 8px;
        font: inherit;
        font-weight: 800;
        cursor: pointer;
      }


      #cvnReviewMonitorRefresh {
        background: #19B5B0;
        color: #07101f;
        border: 1px solid #19B5B0;
      }


      #cvnReviewMonitorClose {
        background: transparent;
        color: #ffffff;
        border: 1px solid #536178;
      }


      @media (max-width: 900px) {

        .cvn-review-governance-grid {
          grid-template-columns: 1fr;
        }

        .cvn-review-summary-grid {
          grid-template-columns:
            repeat(2, minmax(140px, 1fr));
        }
      }


      @media (max-width: 700px) {

        #cvnReviewMonitorOverlay {
          padding: 10px;
        }

        #cvnReviewMonitorPanel {
          width: 98vw;
          max-width: 98vw;
          max-height: 94vh;
          padding: 18px;
        }

        #cvnReviewMonitorPanel h2 {
          font-size: 23px !important;
        }

        .cvn-review-summary-grid {
          grid-template-columns: 1fr;
        }

        .cvn-review-governance-header {
          align-items: flex-start;
          flex-direction: column;
        }
      }
    `;

    document.head.appendChild(style);
  }


  /* =========================================================
     REVIEW GOVERNANCE SUMMARY
  ========================================================= */

  function findMetricsContainer() {
    const adminOverlay =
      document.getElementById(
        'cvnAdminDocsOverlay'
      );

    if (!adminOverlay) {
      return null;
    }

    const metrics =
      adminOverlay.querySelectorAll(
        '.cvn-admin-metric'
      );

    if (!metrics.length) {
      return null;
    }

    return metrics[0].parentElement || null;
  }


  function createGovernanceSection() {
    const metricsContainer =
      findMetricsContainer();

    if (!metricsContainer) {
      return null;
    }

    let section =
      document.getElementById(
        'cvnReviewGovernanceSummary'
      );

    if (section) {
      return section;
    }

    section =
      document.createElement('section');

    section.id =
      'cvnReviewGovernanceSummary';

    section.innerHTML = `

      <div class="cvn-review-governance-header">

        <div class="cvn-review-governance-heading">
          Document Review Governance
        </div>

        <button
          class="cvn-review-governance-link"
          id="cvnReviewGovernanceOpenMonitor"
          type="button"
        >
          Open Review Monitor
        </button>

      </div>

      <div id="cvnReviewGovernanceBody">

        <div id="cvnReviewGovernanceLoading">
          Loading review governance status...
        </div>

      </div>
    `;

    metricsContainer.insertAdjacentElement(
      'afterend',
      section
    );

    document
      .getElementById(
        'cvnReviewGovernanceOpenMonitor'
      )
      ?.addEventListener(
        'click',
        openReviewMonitor
      );

    return section;
  }


  function renderGovernanceSummary(rows) {
    const section =
      createGovernanceSection();

    if (!section) {
      return;
    }

    const body =
      document.getElementById(
        'cvnReviewGovernanceBody'
      );

    if (!body) {
      return;
    }

    const counts =
      getReviewCounts(rows);

    body.innerHTML = `

      <div class="cvn-review-governance-grid">

        <button
          class="
            cvn-review-governance-card
            is-overdue
          "
          type="button"
          data-open-review-monitor="true"
        >

          <span class="cvn-review-governance-card-label">
            Overdue Reviews
          </span>

          <span class="cvn-review-governance-card-value">
            ${counts.overdue}
          </span>

          <span class="cvn-review-governance-card-note">
            Approved versions past their scheduled review date
          </span>

        </button>


        <button
          class="
            cvn-review-governance-card
            is-due-soon
          "
          type="button"
          data-open-review-monitor="true"
        >

          <span class="cvn-review-governance-card-label">
            Due Soon
          </span>

          <span class="cvn-review-governance-card-value">
            ${counts.dueSoon}
          </span>

          <span class="cvn-review-governance-card-note">
            Reviews due within the next 30 days
          </span>

        </button>


        <button
          class="
            cvn-review-governance-card
            is-unscheduled
          "
          type="button"
          data-open-review-monitor="true"
        >

          <span class="cvn-review-governance-card-label">
            Not Scheduled
          </span>

          <span class="cvn-review-governance-card-value">
            ${counts.notScheduled}
          </span>

          <span class="cvn-review-governance-card-note">
            Approved versions without a review due date
          </span>

        </button>

      </div>
    `;

    body
      .querySelectorAll(
        '[data-open-review-monitor="true"]'
      )
      .forEach(
        (button) => {

          button.addEventListener(
            'click',
            openReviewMonitor
          );

        }
      );
  }


  function renderGovernanceError(message) {
    const section =
      createGovernanceSection();

    if (!section) {
      return;
    }

    const body =
      document.getElementById(
        'cvnReviewGovernanceBody'
      );

    if (!body) {
      return;
    }

    body.innerHTML = `

      <div id="cvnReviewGovernanceError">
        ${escapeHtml(
          message ||
          'Unable to load review governance status.'
        )}
      </div>
    `;
  }


  async function loadGovernanceSummary(
    forceRefresh = false
  ) {
    const section =
      createGovernanceSection();

    if (!section) {
      return;
    }

    try {
      const rows =
        await getReviewRows(
          forceRefresh
        );

      renderGovernanceSummary(
        rows
      );

    } catch (error) {
      console.error(
        '[Corevian Review Governance] Unable to load summary:',
        error
      );

      renderGovernanceError(
        error.message
      );
    }
  }


  /* =========================================================
     REVIEW MONITOR MODAL HELPERS
  ========================================================= */

  function closeReviewMonitor() {
    document
      .getElementById(
        'cvnReviewMonitorOverlay'
      )
      ?.remove();
  }


  function showError(message) {
    const element =
      document.getElementById(
        'cvnReviewMonitorMessage'
      );

    if (!element) return;

    element.className = 'error';

    element.textContent =
      message;
  }


  function clearError() {
    const element =
      document.getElementById(
        'cvnReviewMonitorMessage'
      );

    if (!element) return;

    element.className = '';

    element.textContent = '';
  }


  /* =========================================================
     REVIEW MONITOR RENDER
  ========================================================= */

  function renderMonitor(rows) {
    const content =
      document.getElementById(
        'cvnReviewMonitorContent'
      );

    if (!content) return;

    if (!Array.isArray(rows)) {
      throw new Error(
        'The review monitor returned an unexpected response.'
      );
    }

    const counts =
      getReviewCounts(rows);

    const summary = `

      <div class="cvn-review-summary-grid">

        <div class="cvn-review-summary-card">

          <div class="cvn-review-summary-label">
            Overdue
          </div>

          <div class="cvn-review-summary-value">
            ${counts.overdue}
          </div>

        </div>


        <div class="cvn-review-summary-card">

          <div class="cvn-review-summary-label">
            Due Today
          </div>

          <div class="cvn-review-summary-value">
            ${counts.dueToday}
          </div>

        </div>


        <div class="cvn-review-summary-card">

          <div class="cvn-review-summary-label">
            Due Soon
          </div>

          <div class="cvn-review-summary-value">
            ${counts.dueSoon}
          </div>

        </div>


        <div class="cvn-review-summary-card">

          <div class="cvn-review-summary-label">
            Current
          </div>

          <div class="cvn-review-summary-value">
            ${counts.current}
          </div>

        </div>


        <div class="cvn-review-summary-card">

          <div class="cvn-review-summary-label">
            Not Scheduled
          </div>

          <div class="cvn-review-summary-value">
            ${counts.notScheduled}
          </div>

        </div>

      </div>
    `;


    if (!rows.length) {
      content.innerHTML = `

        ${summary}

        <div class="cvn-review-monitor-note">
          No approved controlled-document versions were found.
        </div>
      `;

      return;
    }


    const tableRows = rows
      .map((row) => {

        const status =
          row.review_status ||
          'Not Scheduled';

        return `

          <tr>

            <td>
              <span class="cvn-review-document-code">
                ${escapeHtml(
                  row.document_code
                )}
              </span>
            </td>

            <td class="cvn-review-document-title">
              ${escapeHtml(
                row.title
              )}
            </td>

            <td>
              ${escapeHtml(
                row.department || '—'
              )}
            </td>

            <td>
              <strong>
                ${escapeHtml(
                  row.version_number
                )}
              </strong>
            </td>

            <td>
              ${formatDate(
                row.effective_date
              )}
            </td>

            <td>
              ${formatDate(
                row.review_due_date
              )}
            </td>

            <td>
              <span class="
                cvn-review-status
                ${getStatusClass(status)}
              ">
                ${escapeHtml(status)}
              </span>
            </td>

            <td>
              ${escapeHtml(
                formatDays(row)
              )}
            </td>
             <td>
  ${escapeHtml(
    getFormalReviewOutcomeLabel(
      row.latest_review_outcome
    )
  )}
    <td>
  ${
    row.review_action_status ===
    'Revision Required — Action Pending'
      ? `
        <button
          type="button"
          class="cvn-review-create-revision"
          data-document-code="${escapeHtml(row.document_code)}"
          data-version-number="${escapeHtml(row.version_number)}"
        >
          Create Revision
        </button>
      `
      : escapeHtml(
          row.review_action_status ||
          'No Formal Review'
        )
  }
</td>
            <td>
              ${escapeHtml(
                row.approved_by_name ||
                '—'
              )}
            </td>

            <td>
              ${formatDateTime(
                row.approved_at
              )}
            </td>
            <td>
  <button
    type="button"
    class="cvn-review-row-action"
    data-review-version-id="${escapeHtml(row.document_version_id)}"
  >
    Record Review
  </button>
</td>
          </tr>
        `;
      })
      .join('');


    content.innerHTML = `

      ${summary}

      <div class="cvn-review-table-wrap">

        <table class="cvn-review-table">

          <thead>

            <tr>

              <th>Document Code</th>

              <th>Document Title</th>

              <th>Department</th>

              <th>Version</th>

              <th>Effective Date</th>

              <th>Review Due</th>

              <th>Review Status</th>

             <th>Review Timing</th>

             <th>Latest Review Outcome</th>

             <th>Review Action Status</th>

             <th>Approved By</th>

              <th>Approved At</th>
            
             <th>Action</th>

             </tr>

          </thead>


          <tbody>
            ${tableRows}
          </tbody>

        </table>

      </div>


      <div class="cvn-review-monitor-note">

        Review status is calculated automatically from each approved
        controlled-document version's review due date.

        Documents within 30 days of review are classified as Due Soon.

        Documents without a review due date are classified as
        Not Scheduled rather than being treated as current.

      </div>
    `;
  }
  /* =========================================================
     FORMAL CONTROLLED-DOCUMENT REVIEW
  ========================================================= */

  function injectFormalReviewStyles() {
    if (
      document.getElementById(
        'cvnFormalReviewStyles'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'cvnFormalReviewStyles';

    style.textContent = `

      #cvnFormalReviewOverlay {
        position: fixed;
        inset: 0;
        z-index: 12950;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 22px;
        background: rgba(3,8,18,.92);
        box-sizing: border-box;
      }

      #cvnFormalReviewPanel {
        width: min(760px,96vw);
        max-height: 92vh;
        overflow-y: auto;
        box-sizing: border-box;
        padding: 28px;
        background: #0B1426;
        color: #ffffff;
        border: 1px solid rgba(25,181,176,.42);
        border-radius: 16px;
        box-shadow: 0 24px 70px rgba(0,0,0,.55);
      }

      #cvnFormalReviewPanel h2 {
        margin: 0 0 8px !important;
        color: #ffffff !important;
        font-size: 25px !important;
        font-weight: 800 !important;
        line-height: 1.2 !important;
      }

      .cvn-formal-review-subtitle {
        margin: 0 0 20px;
        color: #b9c6d8;
        font-size: 14px;
        line-height: 1.55;
      }

      .cvn-formal-review-meta {
        margin-bottom: 20px;
        padding: 14px 16px;
        background: #101b2e;
        border: 1px solid #2a3a53;
        border-radius: 10px;
        color: #dce5f0;
        font-size: 13px;
        line-height: 1.6;
      }

      .cvn-formal-review-field {
        margin-bottom: 16px;
      }

      .cvn-formal-review-field label {
        display: block;
        margin-bottom: 7px;
        color: #dbe6f3;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: .03em;
      }

      .cvn-formal-review-field select,
      .cvn-formal-review-field input,
      .cvn-formal-review-field textarea {
        width: 100%;
        box-sizing: border-box;
        padding: 11px 12px;
        border: 1px solid #40516b;
        border-radius: 8px;
        background: #101b2e;
        color: #ffffff;
        font: inherit;
        font-size: 14px;
        outline: none;
      }

      .cvn-formal-review-field textarea {
        min-height: 105px;
        resize: vertical;
      }

      .cvn-formal-review-field select:focus,
      .cvn-formal-review-field input:focus,
      .cvn-formal-review-field textarea:focus {
        border-color: #19B5B0;
      }

      .cvn-formal-review-help {
        margin-top: 6px;
        color: #8fa0b6;
        font-size: 12px;
        line-height: 1.45;
      }

      #cvnFormalReviewMessage {
        display: none;
        margin-bottom: 16px;
        padding: 12px 14px;
        border-radius: 8px;
        font-size: 13px;
      }

      #cvnFormalReviewMessage.error {
        display: block;
        background: rgba(190,65,65,.13);
        border: 1px solid rgba(210,80,80,.45);
        color: #ffd2d2;
      }

      #cvnFormalReviewMessage.success {
        display: block;
        background: rgba(25,181,176,.12);
        border: 1px solid rgba(25,181,176,.42);
        color: #8ef0ec;
      }

      .cvn-formal-review-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        flex-wrap: wrap;
        margin-top: 20px;
      }

      .cvn-formal-review-actions button {
        min-height: 42px;
        padding: 10px 16px;
        border-radius: 8px;
        font: inherit;
        font-weight: 800;
        cursor: pointer;
      }

      #cvnFormalReviewClose {
        background: transparent;
        color: #ffffff;
        border: 1px solid #536178;
      }

      #cvnFormalReviewSave {
        background: #19B5B0;
        color: #07101f;
        border: 1px solid #19B5B0;
      }

      #cvnFormalReviewSave:disabled {
        opacity: .6;
        cursor: wait;
      }

      .cvn-formal-review-history {
        margin-top: 24px;
        padding-top: 20px;
        border-top: 1px solid #2a3a53;
      }

      .cvn-formal-review-history-title {
        margin-bottom: 12px;
        color: #ffffff;
        font-size: 14px;
        font-weight: 800;
      }

      .cvn-formal-review-history-entry {
        margin-bottom: 10px;
        padding: 12px 14px;
        background: #101b2e;
        border: 1px solid #2a3a53;
        border-radius: 9px;
        color: #dce5f0;
        font-size: 12px;
        line-height: 1.6;
      }

      .cvn-formal-review-history-empty {
        color: #9fb0c6;
        font-size: 13px;
      }

      @media (max-width: 700px) {

        #cvnFormalReviewOverlay {
          padding: 10px;
        }

        #cvnFormalReviewPanel {
          width: 98vw;
          max-width: 98vw;
          padding: 18px;
        }
      }

    `;

    document.head.appendChild(style);
  }


  function closeFormalReview() {
    document
      .getElementById(
        'cvnFormalReviewOverlay'
      )
      ?.remove();
  }


  function getFormalReviewOutcomeLabel(value) {
    const labels = {
      no_change_required:
        'No Change Required',

      revision_required:
        'Revision Required',

      retirement_recommended:
        'Retirement Recommended'
    };

    return labels[value] || value || '—';
  }


  async function loadFormalReviewHistory(
    documentVersionId
  ) {
    const container =
      document.getElementById(
        'cvnFormalReviewHistoryBody'
      );

    if (!container) {
      return;
    }

    container.innerHTML = `
      <div class="cvn-formal-review-history-empty">
        Loading formal review history...
      </div>
    `;

    try {
      const rows = await rpc(
        'get_admin_controlled_document_review_history',
        {
          p_document_version_id:
            documentVersionId
        }
      );

      if (
        !Array.isArray(rows) ||
        rows.length === 0
      ) {
        container.innerHTML = `
          <div class="cvn-formal-review-history-empty">
            No formal reviews have been recorded for this version.
          </div>
        `;

        return;
      }

      container.innerHTML =
        rows
          .map((item) => {

            return `

              <div class="cvn-formal-review-history-entry">

                <div>
                  <strong>
                    ${escapeHtml(
                      getFormalReviewOutcomeLabel(
                        item.review_outcome
                      )
                    )}
                  </strong>
                </div>

                <div>
                  Reviewed By:
                  ${escapeHtml(
                    item.reviewed_by_name || '—'
                  )}
                </div>

                <div>
                  Reviewed At:
                  ${formatDateTime(
                    item.reviewed_at
                  )}
                </div>

                <div>
                  Next Review:
                  ${formatDate(
                    item.next_review_date
                  )}
                </div>

                <div>
                  Notes:
                  ${escapeHtml(
                    item.review_notes || '—'
                  )}
                </div>

              </div>
            `;
          })
          .join('');

    } catch (error) {
      console.error(
        '[Corevian Formal Review] History error:',
        error
      );

      container.innerHTML = `
        <div class="cvn-formal-review-history-empty">
          Unable to load formal review history.
        </div>
      `;
    }
  }


  async function saveFormalReview(row) {
    const outcome =
      document.getElementById(
        'cvnFormalReviewOutcome'
      )?.value || '';

    const notes =
      document.getElementById(
        'cvnFormalReviewNotes'
      )?.value.trim() || '';

    const nextReviewDate =
      document.getElementById(
        'cvnFormalReviewNextDate'
      )?.value || null;

    const message =
      document.getElementById(
        'cvnFormalReviewMessage'
      );

    const saveButton =
      document.getElementById(
        'cvnFormalReviewSave'
      );

    if (message) {
      message.className = '';
      message.textContent = '';
    }


    if (!outcome) {
      if (message) {
        message.className = 'error';

        message.textContent =
          'Select a formal review outcome.';
      }

      return;
    }


    if (
      outcome === 'no_change_required' &&
      !nextReviewDate
    ) {
      if (message) {
        message.className = 'error';

        message.textContent =
          'Enter the next review date when no change is required.';
      }

      return;
    }


    if (
      (
        outcome === 'revision_required' ||
        outcome === 'retirement_recommended'
      ) &&
      !notes
    ) {
      if (message) {
        message.className = 'error';

        message.textContent =
          'Review notes are required when revision or retirement is recommended.';
      }

      return;
    }


    if (saveButton) {
      saveButton.disabled = true;

      saveButton.textContent =
        'Recording Review...';
    }


    try {
      await rpc(
        'record_controlled_document_review',
        {
          p_document_version_id:
            row.document_version_id,

          p_review_outcome:
            outcome,

          p_review_notes:
            notes || null,

          p_next_review_date:
            nextReviewDate
        }
      );


      clearReviewCache();


      await loadFormalReviewHistory(
        row.document_version_id
      );


      await loadReviewMonitor(true);


      if (message) {
        message.className = 'success';

        message.textContent =
          'Formal controlled-document review recorded successfully.';
      }


    } catch (error) {
      console.error(
        '[Corevian Formal Review] Save error:',
        error
      );

      if (message) {
        message.className = 'error';

        message.textContent =
          error.message ||
          'Unable to record the formal review.';
      }


    } finally {
      if (saveButton) {
        saveButton.disabled = false;

        saveButton.textContent =
          'Record Review';
      }
    }
  }


  async function openFormalReview(row) {
    injectFormalReviewStyles();

    closeFormalReview();


    const overlay =
      document.createElement('div');


    overlay.id =
      'cvnFormalReviewOverlay';


    overlay.innerHTML = `

      <div
        id="cvnFormalReviewPanel"
        role="dialog"
        aria-modal="true"
      >

        <h2>
          Record Formal Document Review
        </h2>


        <div class="cvn-formal-review-subtitle">

          Record an auditable review decision for this exact
          approved controlled-document version.

        </div>


        <div class="cvn-formal-review-meta">

          <strong>
            ${escapeHtml(
              row.document_code
            )}
          </strong>

          —
          ${escapeHtml(
            row.title
          )}

          <br>

          Version
          ${escapeHtml(
            row.version_number
          )}

          · Current Review Due:
          ${formatDate(
            row.review_due_date
          )}

          · Status:
          ${escapeHtml(
            row.review_status ||
            'Not Scheduled'
          )}

        </div>


        <div
          id="cvnFormalReviewMessage"
        ></div>


        <div class="cvn-formal-review-field">

          <label for="cvnFormalReviewOutcome">
            Review Outcome
          </label>

          <select id="cvnFormalReviewOutcome">

            <option value="">
              Select outcome
            </option>

            <option value="no_change_required">
              No Change Required
            </option>

            <option value="revision_required">
              Revision Required
            </option>

            <option value="retirement_recommended">
              Retirement Recommended
            </option>

          </select>

        </div>


        <div class="cvn-formal-review-field">

          <label for="cvnFormalReviewNotes">
            Review Notes
          </label>

          <textarea
            id="cvnFormalReviewNotes"
            placeholder="Document the review findings, rationale, or required follow-up."
          ></textarea>

          <div class="cvn-formal-review-help">
            Notes are required when revision or retirement is recommended.
          </div>

        </div>


        <div class="cvn-formal-review-field">

          <label for="cvnFormalReviewNextDate">
            Next Review Date
          </label>

          <input
  id="cvnFormalReviewNextDate"
  type="date"
  min="${new Date().toISOString().slice(0, 10)}"
>

          <div class="cvn-formal-review-help">

            Required when No Change Required is selected.
            The approved version's next review date will be updated.

          </div>

        </div>


        <div class="cvn-formal-review-actions">

          <button
            id="cvnFormalReviewClose"
            type="button"
          >
            Close
          </button>

          <button
            id="cvnFormalReviewSave"
            type="button"
          >
            Record Review
          </button>

        </div>


        <div class="cvn-formal-review-history">

          <div class="cvn-formal-review-history-title">
            Formal Review History
          </div>

          <div
            id="cvnFormalReviewHistoryBody"
          >

            <div class="cvn-formal-review-history-empty">
              Loading formal review history...
            </div>

          </div>

        </div>

      </div>
    `;


    document.body.appendChild(
      overlay
    );


    overlay.addEventListener(
      'click',
      function (event) {

        if (
          event.target === overlay
        ) {
          closeFormalReview();
        }
      }
    );


    document
      .getElementById(
        'cvnFormalReviewClose'
      )
      ?.addEventListener(
        'click',
        closeFormalReview
      );


    document
      .getElementById(
        'cvnFormalReviewSave'
      )
      ?.addEventListener(
        'click',
        function () {
          saveFormalReview(row);
        }
      );


    await loadFormalReviewHistory(
      row.document_version_id
    );
  }


   document.addEventListener(
  'click',
  async function (event) {

    const button =
      event.target.closest(
        '.cvn-review-row-action'
      );

    if (!button) {
      return;
    }

    const versionId =
      button.getAttribute(
        'data-review-version-id'
      );

    try {
      const rows =
        await getReviewRows();

      const row =
        rows.find(
          (item) =>
            String(
              item.document_version_id
            ) ===
            String(
              versionId
            )
        );

      if (!row) {
        throw new Error(
          'The selected controlled-document version could not be found.'
        );
      }

      await openFormalReview(
        row
      );

    } catch (error) {
      console.error(
        '[Corevian Formal Review] Open error:',
        error
      );

      alert(
        error.message ||
        'Unable to open the formal review.'
      );
    }
  }
);


document.addEventListener(
  'click',
  function (event) {

    const button =
      event.target.closest(
        '.cvn-review-create-revision'
      );

    if (!button) {
      return;
    }

    const documentCode =
      button.dataset.documentCode;

    const versionNumber =
      button.dataset.versionNumber;

    if (
      typeof window.CorevianOpenRevisionModal !==
      'function'
    ) {
      console.error(
        '[Corevian Review Monitor] Revision modal is not available.'
      );

      return;
    }

    window.CorevianOpenRevisionModal(
      documentCode,
      versionNumber
    );
  }
);

     
  /* =========================================================
     LOAD REVIEW MONITOR
  ========================================================= */

  async function loadReviewMonitor(
    forceRefresh = false
  ) {
    clearError();

    const refreshButton =
      document.getElementById(
        'cvnReviewMonitorRefresh'
      );

    const content =
      document.getElementById(
        'cvnReviewMonitorContent'
      );


    if (refreshButton) {
      refreshButton.disabled = true;

      refreshButton.textContent =
        'Refreshing...';
    }


    if (content) {
      content.innerHTML =
        'Loading review monitoring data...';
    }


    try {
      if (forceRefresh) {
        clearReviewCache();
      }

      const rows =
        await getReviewRows(
          forceRefresh
        );

      renderMonitor(
        rows
      );

      renderGovernanceSummary(
        rows
      );

    } catch (error) {
      console.error(
        '[Corevian Review Monitor] Unable to load monitor:',
        error
      );

      showError(
        error.message ||
        'Unable to load controlled-document review monitoring.'
      );

      if (content) {
        content.innerHTML = '';
      }

    } finally {
      if (refreshButton) {
        refreshButton.disabled = false;

        refreshButton.textContent =
          'Refresh Monitor';
      }
    }
  }


  /* =========================================================
     OPEN REVIEW MONITOR
  ========================================================= */

  async function openReviewMonitor() {
    if (
      document.getElementById(
        'cvnReviewMonitorOverlay'
      )
    ) {
      return;
    }


    const overlay =
      document.createElement('div');

    overlay.id =
      'cvnReviewMonitorOverlay';

    overlay.innerHTML = `

      <div
        id="cvnReviewMonitorPanel"
        role="dialog"
        aria-modal="true"
      >

        <h2>
          Controlled Document Review Monitor
        </h2>

        <div class="cvn-review-monitor-subtitle">

          Monitor approved controlled-document review deadlines,
          upcoming reviews, overdue reviews, and documents without
          a scheduled review date.

        </div>


        <div
          id="cvnReviewMonitorMessage"
        ></div>


        <div
          id="cvnReviewMonitorContent"
        >

          Loading review monitoring data...

        </div>


        <div class="cvn-review-monitor-actions">

          <button
            id="cvnReviewMonitorClose"
            type="button"
          >
            Close
          </button>

          <button
            id="cvnReviewMonitorRefresh"
            type="button"
          >
            Refresh Monitor
          </button>

        </div>

      </div>
    `;


    document.body.appendChild(
      overlay
    );


    overlay.addEventListener(
      'click',
      function (event) {

        if (event.target === overlay) {
          closeReviewMonitor();
        }

      }
    );


    document
      .getElementById(
        'cvnReviewMonitorClose'
      )
      ?.addEventListener(
        'click',
        closeReviewMonitor
      );


    document
      .getElementById(
        'cvnReviewMonitorRefresh'
      )
      ?.addEventListener(
        'click',
        function () {
          loadReviewMonitor(true);
        }
      );


    await loadReviewMonitor();
  }


  /* =========================================================
     INSTALL REVIEW MONITOR BUTTON
  ========================================================= */

  function installLauncher() {
    const adminOverlay =
      document.getElementById(
        'cvnAdminDocsOverlay'
      );


    if (!adminOverlay) {
      return false;
    }


    if (
      document.getElementById(
        'cvnReviewMonitorLauncher'
      )
    ) {
      return true;
    }


    const historyButton =
      document.getElementById(
        'cvnHistoryLauncher'
      );


    const assignButton =
      document.getElementById(
        'cvnAssignLauncher'
      );


    const revisionButton =
      document.getElementById(
        'cvnRevisionLauncher'
      );


    const approveButton =
      document.getElementById(
        'cvnApproveLauncher'
      );


    const reviewButton =
      document.getElementById(
        'cvnSubmitReviewLauncher'
      );


    const createButton =
      document.getElementById(
        'cvnCreateDraftBtn'
      );


    const anchor =
      historyButton ||
      assignButton ||
      revisionButton ||
      approveButton ||
      reviewButton ||
      createButton;


    if (!anchor) {
      return false;
    }


    const button =
      document.createElement(
        'button'
      );


    button.id =
      'cvnReviewMonitorLauncher';


    button.type =
      'button';


    button.textContent =
      'Review Monitor';


    anchor.insertAdjacentElement(
      'afterend',
      button
    );


    button.addEventListener(
      'click',
      openReviewMonitor
    );


    return true;
  }


  /* =========================================================
     INSTALL COMMAND CENTER GOVERNANCE
  ========================================================= */

  function installGovernanceSummary() {
    const adminOverlay =
      document.getElementById(
        'cvnAdminDocsOverlay'
      );

    if (!adminOverlay) {
      return false;
    }

    const section =
      createGovernanceSection();

    if (!section) {
      return false;
    }

    if (
      !section.dataset.loaded
    ) {
      section.dataset.loaded =
        'true';

      loadGovernanceSummary();
    }

    return true;
  }


  /* =========================================================
     START
  ========================================================= */

  function start() {
    injectStyles();

    installLauncher();

    installGovernanceSummary();


    let observerTimer = null;


    const observer =
      new MutationObserver(
        function () {

          clearTimeout(
            observerTimer
          );

          observerTimer =
            setTimeout(
              function () {

                installLauncher();

                installGovernanceSummary();

              },
              80
            );
        }
      );


    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }
    document.addEventListener(
  'click',
  function (event) {
    const button =
      event.target.closest(
        '.cvn-review-create-revision'
      );

    if (!button) return;

    const documentCode =
      button.dataset.documentCode;

    const versionNumber =
      button.dataset.versionNumber;

    if (
      typeof window.CorevianOpenRevisionModal !==
      'function'
    ) {
      console.error(
        '[Corevian Review Monitor] Revision modal is not available.'
      );
      return;
    }

    window.CorevianOpenRevisionModal(
      documentCode,
      versionNumber
    );
  }
);

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start
    );
  } else {
    start();
  }

})();